import { useEffect, useState } from 'react';
import type { CartLine } from './types.ts';

/** v2: `modifiers_cents` is per unit. v1 stored a line total, so v1 carts are dropped. */
const KEY = 'crave-cart-v2';
const OLD_KEYS = ['crave-cart-v1'];
/** The order this cart was last checked out as, so tracking can clear it once that order is paid. */
const PLACED_KEY = 'crave-cart-placed-order';
const LOCATION_KEY = 'crave-location';

function read(): CartLine[] {
  try {
    for (const k of OLD_KEYS) localStorage.removeItem(k);
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (l): l is CartLine =>
        typeof l === 'object' &&
        l !== null &&
        typeof (l as CartLine).menu_item_id === 'string' &&
        typeof (l as CartLine).quantity === 'number' &&
        typeof (l as CartLine).unit_price_cents === 'number' &&
        typeof (l as CartLine).modifiers_cents === 'number',
    );
  } catch {
    return [];
  }
}

function readPlaced(): string | null {
  try {
    return localStorage.getItem(PLACED_KEY);
  } catch {
    return null;
  }
}

let lines: CartLine[] = read();
let placedOrderId: string | null = readPlaced();
const listeners = new Set<() => void>();

function setPlaced(id: string | null) {
  placedOrderId = id;
  try {
    if (id) localStorage.setItem(PLACED_KEY, id);
    else localStorage.removeItem(PLACED_KEY);
  } catch {
    /* private mode */
  }
}

function write(next: CartLine[], opts: { keepPlaced?: boolean } = {}) {
  lines = next;
  // Editing the cart means it no longer matches the order it was checked out as.
  if (!opts.keepPlaced) setPlaced(null);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
  listeners.forEach((l) => l());
}

/** Price of one cart line: base price plus per-unit add-ons, times quantity. */
export function lineTotalCents(l: CartLine): number {
  return (l.unit_price_cents + l.modifiers_cents) * l.quantity;
}

export function cartSubtotalCents(ls: readonly CartLine[]): number {
  return ls.reduce((s, l) => s + lineTotalCents(l), 0);
}

export const cart = {
  get: () => lines,
  add(line: Omit<CartLine, 'key'>) {
    const key = `${line.menu_item_id}:${[...line.modifier_ids].sort().join(',')}`;
    const existing = lines.find((l) => l.key === key);
    write(
      existing
        ? lines.map((l) =>
            l.key === key
              ? {
                  ...l,
                  unit_price_cents: line.unit_price_cents,
                  modifiers_cents: line.modifiers_cents,
                  quantity: Math.min(20, l.quantity + line.quantity),
                }
              : l,
          )
        : [...lines, { ...line, key }],
    );
  },
  setQuantity(key: string, quantity: number) {
    write(
      quantity <= 0
        ? lines.filter((l) => l.key !== key)
        : lines.map((l) => (l.key === key ? { ...l, quantity } : l)),
    );
  },
  clear: () => write([]),
  units: () => lines.reduce((s, l) => s + l.quantity, 0),
  /** Remember that the current cart was checked out as this order. */
  markPlaced(orderId: string) {
    setPlaced(orderId);
  },
  /** Whether the cart is still exactly the one checked out as this order. */
  isPlacedAs: (orderId: string) => placedOrderId === orderId,
  /** Clear the cart if it is still the one checked out as this order. */
  clearIfPlaced(orderId: string): boolean {
    if (placedOrderId !== orderId) return false;
    write([]);
    return true;
  },
};

export function useCart(): CartLine[] {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return lines;
}

/**
 * Where the customer picks up. Remembered on the device; with one location there is nothing to
 * choose. Prices and stock belong to a location, so moving to another one empties the cart.
 */
let pickupLocation: string = (() => {
  try {
    return localStorage.getItem(LOCATION_KEY) ?? 'mobile-1';
  } catch {
    return 'mobile-1';
  }
})();
const locationListeners = new Set<() => void>();

export const orderLocation = {
  get: () => pickupLocation,
  set(slug: string, opts: { keepCart?: boolean } = {}) {
    if (slug === pickupLocation) return;
    pickupLocation = slug;
    try {
      localStorage.setItem(LOCATION_KEY, slug);
    } catch {
      /* private mode */
    }
    if (!opts.keepCart) write([]);
    locationListeners.forEach((l) => l());
  },
};

export function useOrderLocation(): string {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    locationListeners.add(l);
    return () => void locationListeners.delete(l);
  }, []);
  return pickupLocation;
}
